import { memo, useCallback, useEffect, useState } from 'react';

import { SearchOutlined } from '@ant-design/icons';
import { Input, Modal, Spin } from 'antd';
import classnames from 'classnames';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';

import type { CommonReduxState } from '@src/models/commons/reduxState.interface';
import { ALLOWED_ROLES, hasAnyAllowedRole, resolveOrgAccess } from '@src/packages/utils';

import useSearchResultNavigation, {
  SEARCH_RESULT_INDEX_ATTRIBUTE,
} from '../../hooks/useSearchResultNavigation';
import styles from '../../styles.module.less';
import { getEventSearchResultLabels } from '../../utils/eventSearchResult';
import { getSearchEntryRoute } from '../../utils/searchEntryRoute';
import {
  getScheduleSearchResultLabels,
  getTaskSearchResultLabels,
} from '../../utils/scheduleSearchResult';
import { getNameInitials } from '../SidebarBottom/getNameInitials';
import SearchResultAvatar from './SearchResultAvatar';
import useSidebarSearchEntries from './hooks/useSidebarSearchEntries';
import type { MenuSearchItem, SearchEntry } from './types';

const SEARCH_ENTRY_ICONS: Record<string, string> = {
  event: 'event_note',
  schedule: 'calendar_today',
  route: 'route',
  task: 'desktop_mac',
};

interface SidebarSearchProps {
  open: boolean;
  searchableItems: MenuSearchItem[];
  onClose: () => void;
  /** Bumped when search is opened so the filter returns to All without clearing the query. */
  filterResetToken: number;
}

const SidebarSearch = memo(({
  open,
  searchableItems,
  onClose,
  filterResetToken,
}: SidebarSearchProps) => {
  const intl = useIntl();
  const history = useHistory();
  const { url } = useRouteMatch();
  const orgId = useSelector(({ organizations }: CommonReduxState) => organizations?.organization?.id);
  const currentUser = useSelector(({ user }: CommonReduxState) => user?.profile?.data);
  const claims = useSelector(({ user }: CommonReduxState) => user?.access?.data?.claims);
  const currentUserPicture =
    claims && 'picture' in claims && typeof claims.picture === 'string' ? claims.picture : undefined;
  const orgUserAccess = useSelector(
    ({ user }: CommonReduxState) => user?.orgUser?.data?.access,
  );
  const userOrgAccess = resolveOrgAccess(claims, orgId, orgUserAccess);
  const userOrgDivisions = useSelector(
    ({ divisions }: CommonReduxState) => divisions?.assigned?.data?.divisions ?? {},
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [searchFilter, setSearchFilter] = useState('all');

  const canSearchUsers = hasAnyAllowedRole(
    ALLOWED_ROLES.ORGANIZATIONS.USERS.GET,
    userOrgAccess,
    userOrgDivisions,
  );
  const canSearchEvents = hasAnyAllowedRole(
    ALLOWED_ROLES.ORGANIZATIONS.EVENTS_SUMMARY.LIST,
    userOrgAccess,
    userOrgDivisions,
  );
  const canSearchSchedules = hasAnyAllowedRole(
    ALLOWED_ROLES.ORGANIZATIONS.DIVISIONS.SCHEDULES.UPDATE,
    userOrgAccess,
    userOrgDivisions,
  );
  const canSearchTasks = hasAnyAllowedRole(
    ALLOWED_ROLES.ORGANIZATIONS.DIVISIONS.WORK_ORDER.LIST,
    userOrgAccess,
    userOrgDivisions,
  );

  useEffect(() => {
    if (open) return;
    setSearchTerm('');
    setSearchFilter('all');
  }, [open]);

  useEffect(() => {
    setSearchFilter('all');
  }, [filterResetToken]);

  useEffect(() => {
    if (!open) return undefined;

    // Ant Modal moves focus while its opening animation runs; focus after that
    // handoff so the search field stays ready for typing.
    const focusTimeout = window.setTimeout(() => {
      document.querySelector<HTMLInputElement>(`.${styles.searchModal} input`)?.focus();
    }, 350);

    return () => window.clearTimeout(focusTimeout);
  }, [open]);

  const {
    entries,
    loading,
    errorId,
    isRemoteSearchFilter,
    hasRemoteSearchTerm,
  } = useSidebarSearchEntries({
    open,
    orgId,
    searchTerm,
    searchFilter,
    searchableItems,
    canSearchUsers,
    canSearchEvents,
    canSearchSchedules,
    canSearchTasks,
  });

  const openSearchEntry = useCallback(
    (entry: SearchEntry) => {
      const destination = getSearchEntryRoute(entry, url);
      onClose();
      if (!destination) return;
      if (destination.action === 'blank') {
        window.open(destination.path, '_blank', 'noopener,noreferrer');
        return;
      }
      history.push(destination.path);
    },
    [history, onClose, url],
  );

  const normalizedSearchTerm = searchTerm
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase(intl.locale);

  const {
    activeIndex,
    listRef,
    onKeyDown,
    setActiveIndex,
  } = useSearchResultNavigation({
    resultKeys: entries.map(entry => entry.key),
    resetKey: `${searchFilter}\n${normalizedSearchTerm}`,
    onSelect: index => {
      const entry = entries[index];
      if (entry) openSearchEntry(entry);
    },
  });

  const getEntityEntryLabels = (entry: SearchEntry) => {
    if (entry.type === 'event') return getEventSearchResultLabels(entry.event);
    if (entry.type === 'schedule' || entry.type === 'route') {
      return getScheduleSearchResultLabels(
        entry.schedule,
        userOrgDivisions[entry.schedule.divisionId]?.name,
      );
    }
    if (entry.type === 'task') return getTaskSearchResultLabels(entry.task);
    return null;
  };

  const filters = [
    { key: 'all', labelId: 'sidebar.search.filter.all' },
    { key: 'menu', labelId: 'sidebar.search.filter.menu' },
    ...(canSearchUsers ? [{ key: 'users', labelId: 'sidebar.search.filter.users' }] : []),
    ...(canSearchEvents ? [{ key: 'events', labelId: 'sidebar.search.filter.events' }] : []),
    ...(canSearchSchedules
      ? [
          { key: 'schedules', labelId: 'sidebar.search.filter.schedules' },
          { key: 'routes', labelId: 'sidebar.search.filter.routes' },
        ]
      : []),
    ...(canSearchTasks ? [{ key: 'tasks', labelId: 'sidebar.search.filter.tasks' }] : []),
  ];

  return (
    <Modal
      className={styles.searchModal}
      destroyOnClose
      footer={null}
      onCancel={onClose}
      open={open}
      style={{ top: '15vh' }}
      title={null}
      width={720}
    >
      <Input
        aria-activedescendant={activeIndex >= 0 ? `sidebar-search-result-${activeIndex}` : undefined}
        aria-controls="sidebar-search-results"
        aria-label={intl.formatMessage({ id: 'general.search' })}
        autoFocus
        className={styles.searchInput}
        onChange={event => setSearchTerm(event.target.value)}
        onKeyDown={onKeyDown}
        placeholder={intl.formatMessage({ id: 'general.search.placeholder' })}
        prefix={<SearchOutlined aria-hidden />}
        value={searchTerm}
      />
      <div
        aria-label={intl.formatMessage({ id: 'sidebar.search.filters' })}
        className={styles.searchFilters}
        role="tablist"
      >
        {filters.map(filter => (
          <button
            aria-selected={searchFilter === filter.key}
            className={classnames(styles.searchFilter, {
              [styles.searchFilterActive]: searchFilter === filter.key,
            })}
            key={filter.key}
            onClick={() => setSearchFilter(filter.key)}
            role="tab"
            type="button"
          >
            {intl.formatMessage({ id: filter.labelId })}
          </button>
        ))}
      </div>
      <div
        className={styles.searchResults}
        id="sidebar-search-results"
        ref={listRef}
        role="listbox"
      >
        {!searchTerm.trim() ? (
          <p className={styles.searchHint}>{intl.formatMessage({ id: 'sidebar.search.hint' })}</p>
        ) : isRemoteSearchFilter && !hasRemoteSearchTerm ? (
          <p className={styles.searchHint}>{intl.formatMessage({ id: 'sidebar.search.minChars' })}</p>
        ) : loading && !entries.length ? (
          <div className={styles.searchLoading}><Spin size="small" /></div>
        ) : !entries.length ? (
          <p className={styles.searchHint}>
            {intl.formatMessage({ id: errorId ?? 'sidebar.search.noResults' })}
          </p>
        ) : (
          <>
            {entries.map((entry, index) => {
              const entryProps = {
                'aria-selected': index === activeIndex,
                className: classnames(styles.searchResult, {
                  [styles.searchResultActive]: index === activeIndex,
                }),
                id: `sidebar-search-result-${index}`,
                onClick: () => openSearchEntry(entry),
                onMouseMove: () => {
                  if (index !== activeIndex) setActiveIndex(index);
                },
                role: 'option' as const,
                tabIndex: -1,
                type: 'button' as const,
                [SEARCH_RESULT_INDEX_ATTRIBUTE]: index,
              };

              if (entry.type === 'menu') {
                return (
                  <button key={entry.key} {...entryProps}>
                    <span className={styles.searchResultIcon}>{entry.item.icon}</span>
                    <span>{entry.item.label}</span>
                  </button>
                );
              }

              const labels = getEntityEntryLabels(entry);
              if (labels) {
                return (
                  <button key={entry.key} {...entryProps}>
                    <span className={styles.searchResultIcon}>
                      <span aria-hidden className="material-symbols-outlined">
                        {SEARCH_ENTRY_ICONS[entry.type]}
                      </span>
                    </span>
                    <span className={styles.searchResultContent}>
                      <span>{labels.title}</span>
                      {labels.details.length ? (
                        <span className={styles.searchResultSecondary}>
                          {labels.details.join(' · ')}
                        </span>
                      ) : null}
                    </span>
                  </button>
                );
              }

              if (entry.type !== 'user') return null;
              const { user } = entry;
              const displayName = [user.firstName, user.lastName].filter(Boolean).join(' ');
              const label = displayName || user.email || user.id;

              return (
                <button key={entry.key} {...entryProps}>
                  <span className={classnames(styles.searchResultIcon, styles.searchResultUserIcon)}>
                    <SearchResultAvatar
                      initials={getNameInitials(user.firstName, user.lastName) || '?'}
                      photoURL={
                        user.photoURL ||
                        user.avatarURL ||
                        (user.id === currentUser?.id
                          ? currentUserPicture || currentUser?.photoURL
                          : null)
                      }
                    />
                  </span>
                  <span className={styles.searchResultContent}>
                    <span>{label}</span>
                    {user.idCard ? (
                      <span className={styles.searchResultSecondary}>
                        {intl.formatMessage({ id: 'users.list.column.idCard' })}: {user.idCard}
                      </span>
                    ) : null}
                  </span>
                </button>
              );
            })}
            {loading ? <div className={styles.searchLoading}><Spin size="small" /></div> : null}
          </>
        )}
      </div>
    </Modal>
  );
});

export default SidebarSearch;
